/**
 * Tax Product Calculator - Módulo de cálculo de impostos para PDP
 * Calcula DIFAL para produtos específicos (tag: Equipamentos)
 */

class TaxProductCalculator extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    this.isLoading = false;
    this.taxData = null;

    this.debug =
      String(this.dataset?.debug ?? "").toLowerCase() === "true" ||
      window.__TAX_PDP_DEBUG === true;

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleFieldBlur = this.handleFieldBlur.bind(this);
    this.handlePersonTypeChange = this.handlePersonTypeChange.bind(this);
    this.handleExemptChange = this.handleExemptChange.bind(this); 
    this.handleProvinceSelectChange = this.handleProvinceSelectChange.bind(this);
    this.calculateTaxes = this.calculateTaxes.bind(this);
    this.handleCustomerTaxDataChanged = this.handleCustomerTaxDataChanged.bind(this);
    this.handleProductTaxDataCleared = this.handleProductTaxDataCleared.bind(this);
  }

  // Validação de CPF
  validateCPF(cpf) {
    cpf = cpf.replace(/[^\d]/g, '');
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
    
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(cpf.charAt(i)) * (10 - i);
    let digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cpf.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cpf.charAt(i)) * (11 - i);
    digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cpf.charAt(10))) return false;
    
    return true;
  }

  // Validação de CNPJ
  validateCNPJ(cnpj) {
    cnpj = cnpj.replace(/[^\d]/g, '');
    if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
    
    let size = cnpj.length - 2;
    let numbers = cnpj.substring(0, size);
    let digits = cnpj.substring(size);
    let sum = 0;
    let pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      sum += numbers.charAt(size - i) * pos--;
      if (pos < 2) pos = 9;
    }
    
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(0))) return false;
    
    size = size + 1;
    numbers = cnpj.substring(0, size);
    sum = 0;
    pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      sum += numbers.charAt(size - i) * pos--;
      if (pos < 2) pos = 9;
    }
    
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(1))) return false;
    
    return true;
  }

  // Validação de Inscrição Estadual (validação básica)
  validateIE(ie, province) {
    ie = ie.replace(/[^\d]/g, '');
    // Validação mínima: deve ter entre 8 e 14 dígitos
    if (ie.length < 8 || ie.length > 14) return false;
    // Não pode ser tudo igual
    if (/^(\d)\1+$/.test(ie)) return false;
    return true;
  }

  log(type, ...args) {
    if (!this.debug) return;
    const fn = console?.[type] || console.log;
    fn.call(console, ...args);
  }


  connectedCallback() {
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.productCode = this.dataset.productCode || '';
    this.customerEmail = this.dataset.email || '';
    this.customerProvince = this.dataset.province || '';
    this.priceTable = this.dataset.priceTable || 'AAA';
    this.endpoint = this.dataset.endpoint || 'https://shopifyapi.idealdistribuidora.com/api/v1/calculate-taxes';

    this.log('log', '[TAX-PDP] init', {
      productId: this.productId,
      variantId: this.variantId,
      endpoint: this.endpoint,
      debug: this.debug
    });

    this.render();

    this.$module = this.shadowRoot.querySelector('.tax-calculator');
    this.$personType = this.shadowRoot.querySelector('select[name="person-type"]');
    this.$cnpj = this.shadowRoot.querySelector('input[name="cnpj"]');
    this.$ie = this.shadowRoot.querySelector('input[name="ie"]');
    this.$exempt = this.shadowRoot.querySelector('input[name="exempt"]');
    this.$ieField = this.shadowRoot.querySelector('[data-ie-field]');
    this.$docLabel = this.shadowRoot.querySelector('[data-doc-label]');
    this.$docInput = this.shadowRoot.querySelector('[data-doc-input]');
    this.$emailHidden = this.shadowRoot.querySelector('input[name="email"]');
    this.$provinceHidden = this.shadowRoot.querySelector('input[name="province"]');
    this.$provinceWrapper = this.shadowRoot.querySelector('[data-province-select]');
    this.$provinceSelect = this.shadowRoot.querySelector('select[name="province-select"]');
    this.$loadingIndicator = this.shadowRoot.querySelector('.tax-loading');
    this.$taxResult = this.shadowRoot.querySelector('.tax-result');
    this.$error = this.shadowRoot.querySelector('.tax-error');

    // Listeners para eventos customizados do carrinho
    window.addEventListener('product-tax-data-cleared', this.handleProductTaxDataCleared);

    // Preenche email se vier do Liquid
    if (this.customerEmail && this.$emailHidden) {
      this.$emailHidden.value = this.customerEmail;
    }
    
    // Controla exibição do select de estado
    // Se tem província do cliente (logado com endereço), usa ela e esconde select
    // Se não tem, SEMPRE mostra o select (obrigatório para DIFAL)
    if (this.customerProvince) {
      // Tem província do cliente - esconde select e usa o valor
      this.$provinceHidden.value = this.customerProvince;
      this.$provinceWrapper.classList.add('hidden');
    } else {
      // NÃO tem província - MOSTRA o select (obrigatório)
      this.$provinceWrapper.classList.remove('hidden');
    }

    // Event listeners
    this.addEventListeners();

    // Carrega dados salvos
    this.loadSavedFiscalData();
    this.loadSavedTaxData();
  }

  addEventListeners() {
    if (!this.$personType) return;
    
    this.$personType.addEventListener('change', this.handlePersonTypeChange);
    this.$cnpj.addEventListener('input', this.handleFieldChange);
    this.$cnpj.addEventListener('blur', this.handleFieldBlur); // Sincroniza ao sair do campo
    // IE valida apenas no blur/focusout, não durante digitação
    this.$ie.addEventListener('input', this.handleFieldChange);
    this.$ie.addEventListener('blur', this.handleFieldBlur); // Sincroniza ao sair do campo
    this.$exempt.addEventListener('change', this.handleExemptChange);
    this.$provinceSelect?.addEventListener('change', this.handleProvinceSelectChange);
  }

  removeEventListeners() {
    if (!this.$personType) return;
    
    this.$personType.removeEventListener('change', this.handlePersonTypeChange);
    this.$cnpj.removeEventListener('input', this.handleFieldChange);
    this.$cnpj.removeEventListener('blur', this.handleFieldBlur);
    this.$ie.removeEventListener('input', this.handleFieldChange);
    this.$ie.removeEventListener('blur', this.handleFieldBlur);
    this.$exempt.removeEventListener('change', this.handleExemptChange);
    this.$provinceSelect.removeEventListener('change', this.handleProvinceSelectChange);
  }

  disconnectedCallback() {
    this.$personType?.removeEventListener('change', this.handlePersonTypeChange);
    this.$cnpj?.removeEventListener('input', this.handleFieldChange);
    this.$ie?.removeEventListener('blur', this.handleFieldChange);
    this.$exempt?.removeEventListener('change', this.handleExemptChange);
    this.$provinceSelect?.removeEventListener('change', this.handleProvinceSelectChange);
    window.removeEventListener('customer-tax-data-changed', this.handleCustomerTaxDataChanged);
    window.removeEventListener('product-tax-data-cleared', this.handleProductTaxDataCleared);
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
        }
        :host * {
          box-sizing: border-box;
        }
        .hidden { display: none !important; }

        .tax-calculator {
          width: 100%;
        }

        .tax-title {
          font-size: calc(var(--font-body-size) + 2px) !important;
          font-weight: var(--font-heading-weight) !important;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--color-text);
          margin-top: 20px;
          margin-bottom: 10px;
        }

        .tax-fields {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .tax-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .tax-field-with-checkbox {
          position: relative;
        }

        .tax-label {
          font-size: 12px;
          color: #555;
          font-weight: 500;
        }

        .tax-input,
        .tax-select {
          width: 100%;
          padding: 8px 12px;
          border-radius: 4px;
          border: 1px solid #ddd;
          font-size: 14px;
          background: #fff;
          transition: border-color 0.2s;
        }

        .tax-input:focus,
        .tax-select:focus {
          outline: none;
          border-color: #4CAF50;
        }

        .tax-input[disabled] {
          background: #f5f5f5;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .tax-exempt-wrapper {
          display: flex;
          align-items: center;
          gap: 6px;
          position: absolute;
          right: 12px;
          bottom: 8px;
        }

        .tax-exempt-wrapper input[type="checkbox"] {
          width: auto;
          cursor: pointer;
        }

        .tax-exempt-wrapper label {
          font-size: 12px;
          color: #555;
          cursor: pointer;
          user-select: none;
        }

        .tax-loading {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: #fff3cd;
          border-radius: 4px;
          margin-top: 12px;
          font-size: 13px;
          color: #856404;
        }

        .tax-loading .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #856404;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .tax-result {
          padding: 12px;
          background: #d4edda;
          border: 1px solid #c3e6cb;
          border-radius: 4px;
          margin-top: 12px;
        }

        .tax-result-title {
          font-size: 12px;
          color: #155724;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .tax-result-value {
          font-size: 18px;
          color: #155724;
          font-weight: 700;
        }

        .tax-result-note {
          font-size: 11px;
          color: #155724;
          margin-top: 4px;
          opacity: 0.8;
        }

        .tax-error {
          padding: 12px;
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          border-radius: 4px;
          margin-top: 12px;
          font-size: 12px;
          color: #721c24;
        }
      </style>

      <div class="tax-calculator">
        <div class="tax-title">Calcular DIFAL</div>
        
        <div class="tax-fields">
          <div class="tax-field">
            <label class="tax-label" for="tax-person-type">Comprar como</label>
            <select class="tax-select" id="tax-person-type" name="person-type">
              <option value="legal">Pessoa Jurídica</option>
              <option value="physical">Pessoa Física</option>
            </select>
          </div>

          <div class="tax-field">
            <label class="tax-label" for="tax-cnpj" data-doc-label>CNPJ</label>
            <input class="tax-input" id="tax-cnpj" name="cnpj" placeholder="00.000.000/0000-00" data-doc-input />
          </div>

          <div class="tax-field tax-field-with-checkbox" data-ie-field>
            <label class="tax-label" for="tax-ie">Inscrição Estadual</label>
            <input class="tax-input" id="tax-ie" name="ie" placeholder="Inscrição Estadual" />
            <div class="tax-exempt-wrapper">
              <input type="checkbox" id="tax-exempt" name="exempt" />
              <label for="tax-exempt">Isento</label>
            </div>
          </div>

          <div class="tax-field hidden" data-province-select>
            <label class="tax-label" for="tax-province">Estado</label>
            <select class="tax-select" id="tax-province" name="province-select">
              <option value="">Selecione o estado</option>
              <option value="AC">AC</option><option value="AL">AL</option>
              <option value="AP">AP</option><option value="AM">AM</option>
              <option value="BA">BA</option><option value="CE">CE</option>
              <option value="DF">DF</option><option value="ES">ES</option>
              <option value="GO">GO</option><option value="MA">MA</option>
              <option value="MT">MT</option><option value="MS">MS</option>
              <option value="MG">MG</option><option value="PA">PA</option>
              <option value="PB">PB</option><option value="PR">PR</option>
              <option value="PE">PE</option><option value="PI">PI</option>
              <option value="RJ">RJ</option><option value="RN">RN</option>
              <option value="RS">RS</option><option value="RO">RO</option>
              <option value="RR">RR</option><option value="SC">SC</option>
              <option value="SP">SP</option><option value="SE">SE</option>
              <option value="TO">TO</option>
            </select>
          </div>

          <input type="hidden" name="email" />
          <input type="hidden" name="province" />
        </div>

        <div class="tax-loading hidden">
          <div class="spinner"></div>
          <span>Calculando DIFAL...</span>
        </div>

        <div class="tax-result hidden">
          <!--<div class="tax-result-title">DIFAL</div>-->
          <div class="tax-result-value" data-tax-value>R$ 0,00</div>
          <div class="tax-result-note">Valor incluído no preço final</div>
        </div>

        <div class="tax-error hidden"></div>
      </div>
    `;
  }

  loadSavedFiscalData() {
    try {
      this._isLoading = true;
      this._initialLoad = true; // Marca que está no carregamento inicial
      
      // Remove event listeners temporariamente para evitar que atualização programática dispare saves
      this.removeEventListeners();
      
      const raw = localStorage.getItem('customer_tax_data');
      if (!raw) {
        this._isLoading = false;
        this._initialLoad = false;
        this.addEventListeners();
        return;
      }
      
      const data = JSON.parse(raw);
      
      // Carrega tipo de pessoa primeiro (sempre atualiza, tem valor padrão)
      if (this.$personType) {
        const personType = data.personType || 'legal'; // Padrão: Pessoa Jurídica
        this.$personType.value = personType;
        const isPhysical = personType === 'physical';
        
        // Atualiza labels sem limpar campos
        if (this.$docLabel) {
          this.$docLabel.textContent = isPhysical ? 'CPF' : 'CNPJ';
        }
        if (this.$docInput) {
          this.$docInput.placeholder = isPhysical ? '000.000.000-00' : '00.000.000/0000-00';
        }
        if (this.$ieField) {
          this.$ieField.classList.toggle('hidden', isPhysical);
        }
      }
      
      // Carrega CPF/CNPJ (sempre, mesmo se vazio)
      if (this.$cnpj) this.$cnpj.value = data.cnpj || '';
      
      // Carrega checkbox isento primeiro
      if (data.exempt !== undefined && this.$exempt) {
        this.$exempt.checked = data.exempt;
      }
      
      // Carrega IE baseado no estado do checkbox isento
      if (this.$ie) {
        if (data.exempt) {
          // Se isento, limpa e desabilita o campo
          this.$ie.value = '';
          this.$ie.setAttribute('disabled', 'disabled');
        } else {
          // Se não isento, carrega o valor e habilita o campo
          this.$ie.value = data.ie || '';
          this.$ie.removeAttribute('disabled');
        }
      }
      
      // Carrega estado se não veio do Liquid (não logado ou sem endereço)
      if (data.province && !this.customerProvince && this.$provinceSelect) {
        this.$provinceSelect.value = data.province;
        this.$provinceHidden.value = data.province;
      }

      this._isLoading = false;
      
      // Readiciona event listeners
      this.addEventListeners();
      
      // Marca carregamento concluído e adiciona listener de sincronização
      this._initialLoad = false;
      
      // Adiciona listener de sincronização APÓS carregamento completo
      window.addEventListener('customer-tax-data-changed', this.handleCustomerTaxDataChanged);
      
      // Se tem dados completos, calcula automaticamente
      // MAS só se não tiver taxa já salva (loadSavedTaxData roda depois)
      if (this.isDataComplete()) {
        // Aguarda um tick para loadSavedTaxData executar primeiro
        setTimeout(() => {
          if (!this.taxData || this.taxData.totalTaxes === undefined) {
            this.calculateTaxes();
          }
        }, 0);
      }
    } catch (e) {
      console.warn('Erro ao carregar dados fiscais:', e);
      this._isLoading = false;
      this._initialLoad = false;
      this.addEventListeners();
      window.addEventListener('customer-tax-data-changed', this.handleCustomerTaxDataChanged);
    }
  }

  loadSavedTaxData() {
    try {
      const raw = localStorage.getItem('product_tax_data');
      if (!raw) return false;
      
      const allData = JSON.parse(raw);
      const productKey = `product_${this.productId}_variant_${this.variantId}`;
      
      if (allData[productKey]) {
        this.taxData = allData[productKey];
        
        // Extrai breakdown de impostos da resposta salva
        let taxBreakdown = null;
        if (this.taxData.response && this.taxData.response.products && this.taxData.response.products[0]) {
          taxBreakdown = this.taxData.response.products[0].taxs;
        }
        
        this.displayTaxResult(this.taxData.totalTaxes, taxBreakdown);
        this.dispatchTaxEvent(this.taxData.totalTaxes);
        return true; // Indica que encontrou dados salvos
      }
      
      return false; // Não encontrou dados salvos
    } catch (e) {
      console.warn('Erro ao carregar taxas salvas:', e);
      return false;
    }
  }

  saveFiscalData(silent = false) {
    if (this._isSaving) return; // Previne recursão
    
    const personType = this.$personType?.value;
    const cnpj = this.$cnpj?.value || ''; // NÃO usar trim aqui - preserva valor digitado
    const ie = this.$ie?.value || '';
    const exempt = this.$exempt?.checked || false;
    const province = this.$provinceHidden?.value || '';
    
    try {
      this._isSaving = true;
      
      // Salva no mesmo storage usado pelo carrinho para compartilhar dados
      localStorage.setItem('customer_tax_data', JSON.stringify({ 
        personType,
        cnpj, 
        ie,
        exempt,
        province
      }));
      
      // Dispara evento customizado APENAS se:
      // 1. Não for uma sincronização recebida
      // 2. Não for um save silencioso (durante digitação)
      if (!this._receivingSync && !silent) {
        window.dispatchEvent(new CustomEvent('customer-tax-data-changed', {
          detail: { personType, cnpj, ie, exempt, province }
        }));
      }
      
      setTimeout(() => {
        this._isSaving = false;
      }, 0);
    } catch (e) {
      console.warn('Erro ao salvar dados fiscais:', e);
      this._isSaving = false;
    }
  }

  saveTaxData(totalTaxes, apiResponse) {
    try {
      const productKey = `product_${this.productId}_variant_${this.variantId}`;
      
      let allData = {};
      const raw = localStorage.getItem('product_tax_data');
      if (raw) {
        allData = JSON.parse(raw);
      }
      
      // Salva dados fiscais atuais junto com o cache para validação futura
      const fiscalDataRaw = localStorage.getItem('customer_tax_data');
      const fiscalData = fiscalDataRaw ? JSON.parse(fiscalDataRaw) : null;
      
      allData[productKey] = {
        totalTaxes,
        timestamp: Date.now(),
        productId: this.productId,
        variantId: this.variantId,
        response: apiResponse, // Salva resposta completa da API
        fiscalData: fiscalData // Salva dados fiscais usados no cálculo
      };
      
      localStorage.setItem('product_tax_data', JSON.stringify(allData));
      this.taxData = allData[productKey];
    } catch (e) {
      console.warn('Erro ao salvar taxas:', e);
    }
  }

  clearTaxData() {
    try {
      // Limpa TODAS as taxas salvas pois dados fiscais mudaram
      // Isso garante que preços de todos os produtos sejam recalculados
      localStorage.removeItem('product_tax_data');
      
      this.taxData = null;
      this.$taxResult.classList.add('hidden');
      this.dispatchTaxEvent(0);
    } catch (e) {
      console.warn('Erro ao limpar taxas:', e);
    }
  }

  isDataComplete() {
    const cnpj = this.$cnpj?.value.trim();
    const province = this.$provinceHidden?.value.trim();
    const personType = this.$personType?.value;
    const isPhysical = personType === 'physical';
    const isExempt = this.$exempt?.checked;
    const ie = this.$ie?.value.trim();

    // Estado e CPF/CNPJ são sempre obrigatórios
    if (!cnpj || !province) return false;
    
    // Para PJ: IE é obrigatória quando não isento
    if (!isPhysical && !isExempt && !ie) return false;

    return true;
  }

  handlePersonTypeChange() {
    if (this._isLoading || this._receivingSync) return;
    
    const personType = this.$personType.value;
    const isPhysical = personType === 'physical';

    if (this.$docLabel) {
      this.$docLabel.textContent = isPhysical ? 'CPF' : 'CNPJ';
    }
    if (this.$docInput) {
      this.$docInput.placeholder = isPhysical ? '000.000.000-00' : '00.000.000/0000-00';
    }

    if (this.$ieField) {
      this.$ieField.classList.toggle('hidden', isPhysical);
    }

    if (isPhysical) {
      if (this.$ie) this.$ie.value = '';
      if (this.$exempt) this.$exempt.checked = false;
    }

    // Força salvamento imediato ao trocar tipo de pessoa (não aguarda debounce)
    clearTimeout(this.saveTimeout);
    this.saveFiscalData();
    
    // Limpa taxas e recalcula se necessário
    this.clearTaxData();
    if (this.isDataComplete()) {
      clearTimeout(this.calcTimeout);
      this.calcTimeout = setTimeout(() => {
        this.calculateTaxes();
      }, 800);
    }
  }

  handleExemptChange() {
    if (this._isLoading || this._receivingSync) return;
    
    const isExempt = this.$exempt.checked;

    if (isExempt) {
      if (this.$ie) {
        this.$ie.value = '';
        this.$ie.setAttribute('disabled', 'disabled');
      }
    } else {
      if (this.$ie) {
        this.$ie.removeAttribute('disabled');
      }
    }

    // Força salvamento imediato ao mudar checkbox isento
    clearTimeout(this.saveTimeout);
    this.saveFiscalData();
    
    // Limpa taxas e recalcula
    this.clearTaxData();
    if (this.isDataComplete()) {
      clearTimeout(this.calcTimeout);
      this.calcTimeout = setTimeout(() => {
        this.calculateTaxes();
      }, 800);
    }
  }

  handleProvinceSelectChange() {
    if (this._isLoading || this._receivingSync) return;
    
    const value = this.$provinceSelect.value;
    this.$provinceHidden.value = value;
    
    // Força salvamento imediato ao trocar estado
    clearTimeout(this.saveTimeout);
    this.saveFiscalData();
    
    // Limpa taxas e recalcula
    this.clearTaxData();
    if (this.isDataComplete()) {
      clearTimeout(this.calcTimeout);
      this.calcTimeout = setTimeout(() => {
        this.calculateTaxes();
      }, 800);
    }
  }

  handleFieldChange(e) {
    if (this._isLoading || this._receivingSync) return;
    
    // Qualquer alteração dos dados fiscais limpa o storage de taxas
    this.clearTaxData();
    
    // NÃO salva durante digitação - apenas ao sair do campo (blur)
    // Isso evita salvar valores parciais e conflitos com outros módulos
    
    if (this.isDataComplete()) {
      // Debounce para não calcular a cada tecla
      clearTimeout(this.calcTimeout);
      this.calcTimeout = setTimeout(() => {
        this.calculateTaxes();
      }, 800);
    }
  }

  handleFieldBlur(e) {
    if (this._isLoading || this._receivingSync) return;
    
    // Quando campo perde foco: sincroniza com outro módulo
    clearTimeout(this.saveTimeout);
    this.saveFiscalData(false); // Salva E dispara evento de sincronização
  }

  handleCustomerTaxDataChanged(e) {
    // Evento customizado disparado pelo carrinho ao alterar dados fiscais
    if (!e.detail || this._isSaving || this._receivingSync) {
      return;
    }
    
    try {
      this._isLoading = true;
      this._receivingSync = true; // Marca que está recebendo sincronização
      
      // Remove listeners para evitar que atualização programática dispare eventos
      this.removeEventListeners();
      
      const data = e.detail;
      
      // Atualiza campos manualmente sem disparar eventos
      if (this.$personType && data.personType) {
        this.$personType.value = data.personType;
        const isPhysical = data.personType === 'physical';
        
        // Atualiza labels manualmente
        if (this.$docLabel) {
          this.$docLabel.textContent = isPhysical ? 'CPF' : 'CNPJ';
        }
        if (this.$docInput) {
          this.$docInput.placeholder = isPhysical ? '000.000.000-00' : '00.000.000/0000-00';
        }
        if (this.$ieField) {
          this.$ieField.classList.toggle('hidden', isPhysical);
        }
      }
      
      // Sempre sincroniza os valores, mesmo se vazios
      if (this.$cnpj) this.$cnpj.value = data.cnpj || '';
      if (this.$ie) this.$ie.value = data.ie || '';
      
      if (this.$exempt && data.exempt !== undefined) {
        this.$exempt.checked = data.exempt;
        // Atualiza estado do campo IE manualmente
        if (data.exempt && this.$ie) {
          this.$ie.value = '';
          this.$ie.setAttribute('disabled', 'disabled');
        } else if (this.$ie) {
          this.$ie.removeAttribute('disabled');
        }
      }
      
      if (this.$provinceSelect && data.province && !this.customerProvince) {
        this.$provinceSelect.value = data.province;
        this.$provinceHidden.value = data.province;
      }
      
      this._isLoading = false;
      this._receivingSync = false; // Remove flag após processar
      
      // Readiciona listeners
      this.addEventListeners();
      
      // Esconde resultado se estava visível
      if (this.$taxResult) {
        this.$taxResult.classList.add('hidden');
      }
      this.hideError();
      
      // Recalcula se dados estiverem completos
      if (this.isDataComplete()) {
        clearTimeout(this.calcTimeout);
        this.calcTimeout = setTimeout(() => {
          this.calculateTaxes();
        }, 800);
      }
    } catch (err) {
      console.warn('Erro ao processar evento de mudança de dados fiscais:', err);
      this._isLoading = false;
      this.addEventListeners();
    }
  }

  handleProductTaxDataCleared() {
    // Evento disparado quando o carrinho limpa as taxas
    this.taxData = null;
    if (this.$taxResult) {
      this.$taxResult.classList.add('hidden');
    }
    this.dispatchTaxEvent(0);
  }

  async calculateTaxes() {
    if (!this.isDataComplete() || this.isLoading) return;

    // Verifica se já tem taxa calculada salva para este produto/variant
    // E se os dados fiscais são os mesmos (para evitar usar cache inválido)
    if (this.taxData && this.taxData.totalTaxes !== undefined) {
      // Valida se dados fiscais são os mesmos do cache
      const currentFiscalData = localStorage.getItem('customer_tax_data');
      if (currentFiscalData && this.taxData.fiscalData) {
        const current = JSON.parse(currentFiscalData);
        const cached = this.taxData.fiscalData;
        
        // Compara dados fiscais (trim nos valores para comparação)
        const isSameFiscalData = 
          current.personType === cached.personType &&
          (current.cnpj || '').trim() === (cached.cnpj || '').trim() &&
          (current.ie || '').trim() === (cached.ie || '').trim() &&
          current.exempt === cached.exempt &&
          (current.province || '').trim() === (cached.province || '').trim();
        
        if (isSameFiscalData) {
          this.log('log', '[TAX-PDP] ✓✓ Usando taxa do cache (dados fiscais inalterados)');
          this.log('log', `[TAX-PDP] Product: ${this.productId}, Variant: ${this.variantId}`);
          this.log('log', `[TAX-PDP] Taxa em cache: ${this.taxData.totalTaxes} centavos`);
          return;
        } else {
          this.log('warn', '[TAX-PDP] ⚠ Dados fiscais mudaram - cache inválido, fazendo nova requisição');
        }
      }
    }
    
    this.log('log', `[TAX-PDP] Fazendo requisição para Product: ${this.productId} Variant: ${this.variantId}`);

    // Valida CPF/CNPJ
    const cnpj = this.$cnpj.value.trim();
    const personType = this.$personType.value;
    const isPhysical = personType === 'physical';
    
    if (isPhysical) {
      if (!this.validateCPF(cnpj)) {
        this.showError('CPF inválido. Verifique o número digitado.');
        return;
      }
    } else {
      if (!this.validateCNPJ(cnpj)) {
        this.showError('CNPJ inválido. Verifique o número digitado.');
        return;
      }
    }
    
    // Valida IE se não for isento
    const isExempt = this.$exempt.checked;
    const ie = this.$ie.value.trim();
    const province = this.$provinceHidden.value.trim();
    
    if (!isPhysical && !isExempt && ie) {
      if (!this.validateIE(ie, province)) {
        this.showError('Inscrição Estadual inválida. Verifique o número digitado.');
        return;
      }
    }

    this.isLoading = true;
    this.$loadingIndicator.classList.remove('hidden');
    this.$taxResult.classList.add('hidden');
    this.hideError();

    try {
      const body = {
        client: {
          province: this.$provinceHidden.value.trim(),
          provinceTaxCode: '',
          company: this.$cnpj.value.trim()
        },
        products: [{
          variantId: `gid://shopify/ProductVariant/${this.variantId}`,
          code: this.productCode,
          quantity: 1,
          priceTable: this.priceTable,
          consult: true
        }]
      };

      
      this.log('log', `[TAX-PDP] >>> POST: ${this.endpoint}`);
      this.log('log', `[TAX-PDP] Payload:`, body);

      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        this.log('error', `[TAX-PDP] ✗ Erro na requisição - Status: ${res.status}`);
        throw new Error(`Erro HTTP ${res.status}`);
      }

      const data = await res.json();
      this.log('log', `[TAX-PDP] ✓ Resposta da API recebida:`, data);
      
      // Calcula total de impostos da resposta
      if (data && data.products && data.products.length > 0) {
        const product = data.products[0];
        let totalTaxes = 0;
        
        if (product.taxs && Array.isArray(product.taxs)) {
          totalTaxes = product.taxs.reduce((sum, tax) => sum + (tax.value || 0), 0);
        }
        
        // Converte para centavos se necessário (assumindo que a API retorna em reais)
        const totalTaxesCents = Math.round(totalTaxes * 100);
        
        this.log('log', '[TAX-PDP] ✓ Taxa calculada:', totalTaxesCents, 'centavos');
        this.log('log', '[TAX-PDP] Salvando no cache...');

        this.saveTaxData(totalTaxesCents, data);
        this.displayTaxResult(totalTaxesCents, product.taxs);
        this.dispatchTaxEvent(totalTaxesCents);

        this.log('log', '[TAX-PDP] ✓ Dados salvos no localStorage');
      }
    } catch (error) {
      this.log('error', '[TAX-PDP] Erro ao calcular impostos:', error);
      this.showError('Não foi possível calcular os impostos. Tente novamente.');
    } finally {
      this.isLoading = false;
      this.$loadingIndicator.classList.add('hidden');
    }
  }

  displayTaxResult(totalTaxes, taxBreakdown) {
    const valueEl = this.$taxResult.querySelector('[data-tax-value]');
    if (valueEl) {
      valueEl.textContent = this.formatMoney(totalTaxes);
    }
    
    // Adiciona detalhamento dos impostos se disponível
    const noteEl = this.$taxResult.querySelector('.tax-result-note');
    if (noteEl && taxBreakdown && taxBreakdown.length > 0) {
      const breakdown = taxBreakdown.map(tax => 
        `${tax.type}: ${tax.aliquot}% (${this.formatMoney(Math.round(tax.value * 100))})`
      ).join(', ');
      noteEl.textContent = breakdown;
    }
    
    this.$taxResult.classList.remove('hidden');
  }

  showError(message) {
    if (this.$error) {
      this.$error.textContent = message;
      this.$error.classList.remove('hidden');
    }
    this.log('error', '[TAX-PDP] UI error:', message);
  }

  hideError() {
    if (this.$error) {
      this.$error.textContent = '';
      this.$error.classList.add('hidden');
    }
  }

  formatMoney(cents) {
    const reais = cents / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(reais);
  }

  dispatchTaxEvent(totalTaxes) {
    this.dispatchEvent(new CustomEvent('tax-calculated', {
      bubbles: true,
      composed: true,
      detail: {
        productId: this.productId,
        variantId: this.variantId,
        totalTaxes: totalTaxes
      }
    }));
  }

  getTaxAmount() {
    return this.taxData?.totalTaxes || 0;
  }
}

if (!customElements.get('tax-product-calculator')) {
  customElements.define('tax-product-calculator', TaxProductCalculator);
}
